import { useState } from "react";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import CustomButton from "../../components/Button/CustomButton";
import { getStatusChip } from "./ReservationList";
import type { ReservationDetail as ReservationDetailType } from "./types";
import styles from "./ReservationDetail.module.scss";

interface ReservationDetailProps {
  open: boolean;
  reservation: ReservationDetailType | null;
  isLoading?: boolean;
  canApprove: boolean;
  canFinalize: boolean;
  onClose: () => void;
  onApprove: (id: number, reason?: string) => Promise<void>;
  onFinalize: (id: number, notes?: string) => Promise<void>;
  onReject: (id: number, reason?: string) => Promise<void>;
}

export function ReservationDetail({
  open,
  reservation,
  isLoading = false,
  canApprove,
  canFinalize,
  onClose,
  onApprove,
  onFinalize,
  onReject,
}: ReservationDetailProps) {
  const [showApproveInput, setShowApproveInput] = useState(false);
  const [approveReason, setApproveReason] = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showFinalizeInput, setShowFinalizeInput] = useState(false);
  const [finalizeNotes, setFinalizeNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!reservation && !isLoading) return null;

  const isPending = reservation?.status === "pending";
  const isApproved = reservation?.status === "approved";
  const showActions = canApprove && isPending;
  const showFinalizeAction = canFinalize && isApproved;

  const handleApprove = async () => {
    if (!reservation) return;
    setIsSubmitting(true);
    try {
      await onApprove(reservation.id, approveReason.trim() || undefined);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!reservation) return;
    setIsSubmitting(true);
    try {
      await onReject(reservation.id, rejectReason.trim() || undefined);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFinalize = async () => {
    if (!reservation) return;
    setIsSubmitting(true);
    try {
      await onFinalize(reservation.id, finalizeNotes.trim() || undefined);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Reserva #{reservation?.id} — {reservation ? getStatusChip(reservation.status) : null}
      </DialogTitle>

      <DialogContent>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <CircularProgress />
          </div>
        ) : reservation ? (
          <>
            {/* Header info */}
            <div className={styles.headerInfo}>
              {reservation.quote_number && (
                <Typography variant="body2" color="textSecondary">
                  Cotización: {reservation.quote_number}
                </Typography>
              )}
              {reservation.notes && (
                <Typography variant="body2" color="textSecondary">
                  Notas: {reservation.notes}
                </Typography>
              )}
              <Typography variant="body2" color="textSecondary">
                Creada: {new Date(reservation.created_at).toLocaleString("es-CO")}
              </Typography>
              {reservation.approved_at && (
                <Typography variant="body2" color="textSecondary">
                  Aprobada:{" "}
                  {new Date(reservation.approved_at).toLocaleString("es-CO")}
                </Typography>
              )}
              {reservation.rejected_at && (
                <Typography variant="body2" color="textSecondary">
                  Rechazada:{" "}
                  {new Date(reservation.rejected_at).toLocaleString("es-CO")}
                  {reservation.rejection_reason &&
                    ` — ${reservation.rejection_reason}`}
                </Typography>
              )}
              {reservation.finalized_at && (
                <Typography variant="body2" color="textSecondary">
                  Finalizada: {new Date(reservation.finalized_at).toLocaleString("es-CO")}
                  {reservation.finalization_notes &&
                    ` — ${reservation.finalization_notes}`}
                </Typography>
              )}
            </div>

            {/* Items table */}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Producto</TableCell>
                  <TableCell>Bodega</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                  {isPending && (
                    <TableCell align="right">Stock disponible</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {reservation.items?.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.product_name}</TableCell>
                    <TableCell>{item.address_name}</TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    {isPending && (
                      <TableCell align="right">{item.available_stock}</TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        ) : null}

        {/* Approve reason input */}
        {showApproveInput && (
          <TextField
            label="Motivo de aprobación (opcional)"
            variant="outlined"
            fullWidth
            multiline
            minRows={2}
            margin="normal"
            value={approveReason}
            onChange={(e) => setApproveReason(e.target.value)}
            inputProps={{ "aria-label": "motivo-aprobacion" }}
          />
        )}

        {/* Reject reason input */}
        {showRejectInput && (
          <TextField
            label="Motivo de rechazo"
            variant="outlined"
            fullWidth
            multiline
            minRows={2}
            margin="normal"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            inputProps={{ "aria-label": "motivo-rechazo" }}
          />
        )}

        {showFinalizeInput && (
          <TextField
            label="Notas de finalización (opcional)"
            variant="outlined"
            fullWidth
            multiline
            minRows={2}
            margin="normal"
            value={finalizeNotes}
            onChange={(e) => setFinalizeNotes(e.target.value)}
            inputProps={{ "aria-label": "notas-finalizacion" }}
          />
        )}
      </DialogContent>

      <DialogActions>
        <CustomButton color="outlined" onClick={onClose} disabled={isSubmitting}>
          Cerrar
        </CustomButton>

        {showActions && !showRejectInput && !showApproveInput && (
          <>
            <CustomButton
              onClick={() => setShowApproveInput(true)}
              color="primary"
              disabled={isSubmitting}
              icon={CheckCircleIcon}
            >
              Aprobar
            </CustomButton>
            <CustomButton
              onClick={() => setShowRejectInput(true)}
              color="outlined"
              disabled={isSubmitting}
              icon={CancelIcon}
            >
              Rechazar
            </CustomButton>
          </>
        )}

        {showFinalizeAction &&
          !showRejectInput &&
          !showApproveInput &&
          !showFinalizeInput && (
            <CustomButton
              onClick={() => setShowFinalizeInput(true)}
              color="primary"
              disabled={isSubmitting}
              icon={LocalShippingIcon}
            >
              Finalizar
            </CustomButton>
          )}

        {showApproveInput && (
          <>
            <CustomButton
              onClick={() => {
                setShowApproveInput(false);
                setApproveReason("");
              }}
              color="outlined"
              disabled={isSubmitting}
            >
              Cancelar
            </CustomButton>
            <CustomButton
              onClick={handleApprove}
              color="primary"
              disabled={isSubmitting}
              isLoading={isSubmitting}
              icon={CheckCircleIcon}
            >
              Confirmar aprobación
            </CustomButton>
          </>
        )}

        {showRejectInput && (
          <>
            <CustomButton
              onClick={() => {
                setShowRejectInput(false);
                setRejectReason("");
              }}
              color="outlined"
              disabled={isSubmitting}
            >
              Cancelar
            </CustomButton>
            <CustomButton
              onClick={handleReject}
              color="secondary"
              disabled={isSubmitting}
              isLoading={isSubmitting}
              icon={CancelIcon}
            >
              Confirmar rechazo
            </CustomButton>
          </>
        )}

        {showFinalizeInput && (
          <>
            <CustomButton
              onClick={() => {
                setShowFinalizeInput(false);
                setFinalizeNotes("");
              }}
              color="outlined"
              disabled={isSubmitting}
            >
              Cancelar
            </CustomButton>
            <CustomButton
              onClick={handleFinalize}
              color="primary"
              disabled={isSubmitting}
              isLoading={isSubmitting}
              icon={LocalShippingIcon}
            >
              Confirmar finalización
            </CustomButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ReservationDetail;
